// Scenarios whose controls are performed by people and depend on nothing else — the cases that show
// the model staying out of the way, and the case where a period is left uncovered.
//
// Titles and taglines track the design document so a walkthrough can follow it section by section.
import {DemoControl, Scenario} from "../model";

const FY26 = {from: "2026-01-01", to: "2026-12-31"};

const entityControl = (ref: string, title: string): DemoControl => ({
  ref,
  title,
  assessment: "Financial Controls",
  scopes: [{scope: "Global", design: "effective", implementation: "effective"}],
});

export const bankReconciliation: Scenario = {
  id: "s1",
  section: "Scenario 1",
  title: "Monthly bank reconciliation review",
  tagline: "does the simple case stay simple?",
  situation:
    "BR-04, manual, performed monthly by the Financial Controller. Single scope. FY26. No IT " +
    "dependency, no external work.",
  focus: {control: "BR-04", scope: "Global"},
  controls: [entityControl("BR-04", "Bank reconciliation review")],
  edges: [],
  oe: [
    {
      control: "BR-04",
      scope: "Global",
      period: FY26,
      basis: [
        {
          id: "b1",
          status: "obtained" as const,
          span: FY26,
          evidence: {
            kind: "test",
            label: "BR-04 monthly review",
            itemsTested: 2,
            exceptions: 0,
            outcome: "operated_effectively",
            conclusion:
              "Both reviews tested were performed within five working days of month end and evidenced " +
              "by the Financial Controller's sign-off, with all reconciling items cleared.",
          },
          conditions: [],
        },
      ],
      conclusion: {
        outcome: "operated_effectively",
        supportsReliance: true,
        justification:
          "Two of the twelve monthly reconciliation reviews were tested across the period, both " +
          "performed and evidenced by the Financial Controller with no exceptions noted. The control " +
          "is concluded to have operated effectively throughout FY26.",
      },
    },
  ],
};

const payrollInterim = {
  id: "b1",
  status: "obtained" as const,
  span: {from: "2026-01-01", to: "2026-09-30"},
  evidence: {
    kind: "test" as const,
    label: "PAY-02 interim",
    itemsTested: 25,
    exceptions: 0,
    outcome: "operated_effectively" as const,
    conclusion: "All 25 payroll changes tested carried documented approval before the run was released.",
  },
  conditions: [],
};

export const interimRollforward: Scenario = {
  id: "s3",
  section: "Scenario 3",
  title: "Interim testing rolled forward to year end",
  tagline: "the span case",
  situation:
    "PAY-02, payroll change approval, tested at interim in October covering January–September " +
    "(sample of 25). Year end 31 December.",
  focus: {control: "PAY-02", scope: "Global"},
  controls: [entityControl("PAY-02", "Payroll change approval")],
  edges: [],
  oe: [
    {
      control: "PAY-02",
      scope: "Global",
      period: FY26,
      basis: [
        payrollInterim,
        {
          id: "b2",
          status: "obtained" as const,
          span: {from: "2026-10-01", to: "2026-12-31"},
          evidence: {
            kind: "test",
            label: "PAY-02 rollforward",
            itemsTested: 8,
            exceptions: 0,
            outcome: "operated_effectively",
            conclusion: "A further 8 changes from October to December were tested, all approved before release.",
          },
          conditions: [
            {
              kind: "note",
              text: "No change in the control or personnel since interim",
              met: true,
              assertedBy: "R. Mehta",
              assertedAt: "09 Jan 2027",
            },
          ],
        },
      ],
      conclusion: {
        outcome: "operated_effectively",
        supportsReliance: true,
        justification:
          "Interim testing of 25 payroll change approvals covered January to September with no " +
          "exceptions. A further 8 approvals were tested at year end to roll the conclusion forward, " +
          "with no change to the control or the personnel performing it. The period is covered in " +
          "full and the control is concluded effective.",
      },
    },
  ],
};

export const interimGap: Scenario = {
  ...interimRollforward,
  id: "s3-gap",
  section: "Scenario 3 · stress",
  title: "…concluded without the rollforward",
  tagline: "the gap the auditor forgot",
  situation:
    "The same engagement, concluded straight after the interim test. October to December was never " +
    "covered, and nothing in today's model would say so.",
  oe: [
    {
      ...interimRollforward.oe[0],
      basis: [payrollInterim],
      conclusion: {
        outcome: "insufficient_evidence",
        supportsReliance: false,
        justification:
          "Interim testing of 25 payroll change approvals covered January to September with no " + "exceptions.",
      },
    },
  ],
};

export const journalExceptions: Scenario = {
  id: "s6",
  section: "Scenario 6",
  title: "Exceptions, and a control that fails",
  tagline: "where the arithmetic lives",
  situation:
    "JE-01, manual journal authorisation. Sample of 25 from a population of 12,480. Two exceptions: " +
    "journals posted with no recorded approver. Tolerable deviation rate 5%; observed 8%.",
  focus: {control: "JE-01", scope: "Global"},
  controls: [entityControl("JE-01", "Manual journal authorisation")],
  edges: [],
  oe: [
    {
      control: "JE-01",
      scope: "Global",
      period: FY26,
      basis: [
        {
          id: "b1",
          status: "obtained" as const,
          span: FY26,
          evidence: {
            kind: "test",
            label: "JE-01 journal authorisation",
            itemsTested: 25,
            exceptions: 2,
            deviationRate: 8,
            tolerableRate: 5,
            outcome: "did_not_operate_effectively",
            conclusion:
              "Two of the 25 journals tested carried no approver. Both were posted by the same preparer " +
              "but in different months, so the deviations are not isolated to a single occurrence.",
          },
          conditions: [],
        },
      ],
      conclusion: {
        outcome: "did_not_operate_effectively",
        supportsReliance: false,
        justification:
          "Of 25 journals tested, two were posted with no recorded approver — a deviation rate of 8% " +
          "against a tolerable rate of 5%. The exceptions are not isolated to a single preparer or " +
          "period. The control is concluded not to have operated effectively, and no reliance is " +
          "placed on it; substantive testing over the affected assertions is extended accordingly.",
      },
    },
  ],
};
